/**
 * User Menu Component - Simple Shell Theme
 * Displays user information (SSO managed by Microsoft Entra ID)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ADMIN_PERSONA, WELCOME_PAGE_ROUTE } from '../constants/persona';
import { useAuth } from '../contexts/AuthContext';
import { useUserProfile } from '../contexts/UserProfileContext';

const PROFILE_ROUTE = '/profile';
const KNOWLEDGE_BASE_ROUTE = '/knowledge-base';
const USAGE_STATISTICS_ROUTE = '/admin/usage-statistics';
const WELCOME_PAGE_LABEL = 'Welcome Page';
const KNOWLEDGE_BASE_LABEL = 'Knowledge Base';
const PROFILE_PAGE_LABEL = 'My Profile';
const USAGE_STATISTICS_LABEL = 'Usage Statistics';

const COMMA_SEPARATOR = ',';
const EMPTY_VALUE = '';
const DEFAULT_INITIALS = 'U';
const MAX_INITIALS_LENGTH = 2;
const USERNAME_DELIMITERS_REGEX = /[._-]+/;
const WHITESPACE_REGEX = /\s+/;
const NON_ALPHA_REGEX = /[^A-Za-z]/g;
const OPAQUE_IDENTIFIER_REGEX = /^[A-Za-z0-9_-]{24,}$/;
const FALLBACK_DISPLAY_NAME = 'Shell User';

const getAlphaInitial = (value: string): string => {
  const cleanedValue = value.replace(NON_ALPHA_REGEX, EMPTY_VALUE);
  return cleanedValue.charAt(0).toUpperCase();
};

const getFirstTokenInitial = (value: string): string => {
  const firstToken = value.trim().split(WHITESPACE_REGEX)[0] ?? EMPTY_VALUE;
  return getAlphaInitial(firstToken);
};

const getInitialsFromName = (name: string): string => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return EMPTY_VALUE;
  }

  if (trimmedName.includes(COMMA_SEPARATOR)) {
    const [lastNamePart, rightPart] = trimmedName.split(COMMA_SEPARATOR, 2);
    const firstInitial = getFirstTokenInitial(rightPart ?? EMPTY_VALUE);
    const lastInitial = getFirstTokenInitial(lastNamePart ?? EMPTY_VALUE);
    return `${firstInitial}${lastInitial}`.slice(0, MAX_INITIALS_LENGTH);
  }

  const nameParts = trimmedName
    .split(WHITESPACE_REGEX)
    .map((part) => part.trim())
    .filter(Boolean);

  if (nameParts.length >= 2) {
    const firstInitial = getAlphaInitial(nameParts[0]);
    const lastInitial = getAlphaInitial(nameParts[nameParts.length - 1]);
    return `${firstInitial}${lastInitial}`.slice(0, MAX_INITIALS_LENGTH);
  }

  return nameParts[0]
    .replace(NON_ALPHA_REGEX, EMPTY_VALUE)
    .slice(0, MAX_INITIALS_LENGTH)
    .toUpperCase();
};

const getInitialsFromUsername = (username: string): string => {
  const trimmedUsername = username.trim();
  if (!trimmedUsername) {
    return EMPTY_VALUE;
  }

  if (OPAQUE_IDENTIFIER_REGEX.test(trimmedUsername)) {
    return EMPTY_VALUE;
  }

  const localPart = trimmedUsername.split('@')[0] ?? trimmedUsername;
  const parts = localPart
    .split(USERNAME_DELIMITERS_REGEX)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${getAlphaInitial(parts[0])}${getAlphaInitial(parts[1])}`;
  }

  return localPart
    .replace(NON_ALPHA_REGEX, EMPTY_VALUE)
    .slice(0, MAX_INITIALS_LENGTH)
    .toUpperCase();
};

export const getUserInitials = (
  name?: string,
  username?: string,
  email?: string,
): string => {
  const initialsFromName = getInitialsFromName(name ?? EMPTY_VALUE);
  if (initialsFromName) {
    return initialsFromName;
  }

  const initialsFromEmail = getInitialsFromUsername(email ?? EMPTY_VALUE);
  if (initialsFromEmail) {
    return initialsFromEmail;
  }

  const initialsFromUsername = getInitialsFromUsername(username ?? EMPTY_VALUE);
  return initialsFromUsername || DEFAULT_INITIALS;
};

const getDisplayName = (name?: string, email?: string, username?: string): string => {
  const trimmedName = name?.trim();
  if (trimmedName) {
    return trimmedName;
  }

  const trimmedEmail = email?.trim();
  if (trimmedEmail) {
    return trimmedEmail;
  }

  const trimmedUsername = username?.trim() ?? EMPTY_VALUE;
  if (!trimmedUsername || OPAQUE_IDENTIFIER_REGEX.test(trimmedUsername)) {
    return FALLBACK_DISPLAY_NAME;
  }

  return trimmedUsername;
};

const UserMenu: React.FC = () => {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  if (!user) {
    return null;
  }

  // Get username from EntraIDUser
  const username = user.username;
  const userInitials = getUserInitials(user.name, username, user.email);
  const displayName = getDisplayName(user.name, user.email, username);
  const showUsageStatistics = profile?.persona === ADMIN_PERSONA;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-2 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
        aria-label="User menu"
      >
        <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
          <span className="text-white text-sm font-medium">
            {userInitials}
          </span>
        </div>
      </button>
      
      {isOpen && (
        <>
          <button
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            aria-label="Close menu"
            style={{ background: 'transparent', border: 'none' }}
          />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
            <div className="py-1">
              <div className="px-4 py-3 text-sm text-gray-900 border-b border-gray-100">
                <div className="font-medium">{displayName}</div>
                <div className="text-xs text-gray-500 mt-1">Authenticated via EntraID</div>
              </div>
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setIsOpen(false);
                  navigate(PROFILE_ROUTE);
                }}
              >
                {PROFILE_PAGE_LABEL}
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setIsOpen(false);
                  navigate(WELCOME_PAGE_ROUTE);
                }}
              >
                {WELCOME_PAGE_LABEL}
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setIsOpen(false);
                  navigate(KNOWLEDGE_BASE_ROUTE);
                }}
              >
                {KNOWLEDGE_BASE_LABEL}
              </button>
              {showUsageStatistics && (
                <button
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setIsOpen(false);
                    navigate(USAGE_STATISTICS_ROUTE);
                  }}
                >
                  {USAGE_STATISTICS_LABEL}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;
